/**
 * Tournament Utility Functions
 * Shared functions for tournament management
 */

import { collection, addDoc, updateDoc, doc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * Create a tournament
 */
export async function createTournament(tournamentData) {
  const tournamentRef = await addDoc(collection(db, 'tournaments'), {
    ...tournamentData,
    gameId: tournamentData.gameId || 'table-tennis',
    startDate: tournamentData.startDate ? Timestamp.fromDate(new Date(tournamentData.startDate)) : Timestamp.now(),
    createdAt: Timestamp.now()
  });
  return tournamentRef.id;
}

/**
 * Create player registration
 */
export async function createRegistration(tournamentId, playerId, playerEmail, type, skillLevel, skillLevelValue) {
  const registrationData = {
    tournamentId,
    playerId,
    playerEmail,
    type,
    skillLevel,
    skillLevelValue,
    registeredAt: Timestamp.now(),
    status: 'registered'
  };
  
  // Only add teamId for doubles (set to null initially, will be assigned later)
  // For singles, don't include the field at all
  if (type === 'doubles') {
    registrationData.teamId = null;
  }
  
  const registrationRef = await addDoc(collection(db, 'registrations'), registrationData);
  return registrationRef.id;
}

/**
 * Create balanced doubles teams based on skill level.
 * When player count is odd, the player ignored is the one whose first login is last (most recent).
 * @param {Array} registrations - Doubles registrations
 * @param {Object} [firstLoginByPlayerId] - Optional map of playerId -> first-login time (ms). When provided, the user with latest first login is excluded when count is odd.
 */
export function createBalancedTeams(registrations, firstLoginByPlayerId = {}) {
  // Sort players by skill level (beginner=1, average=2, advanced=3)
  const players = registrations.map(reg => ({
    playerId: reg.playerId,
    skillLevel: reg.skillLevel || 'average',
    skillLevelValue: reg.skillLevelValue ||
      (reg.skillLevel === 'beginner' ? 1 :
       reg.skillLevel === 'average' ? 2 : 3)
  }));

  const teams = [];
  const n = players.length;
  const teamCount = Math.floor(n / 2);

  // If odd, ignore the player whose first login is last (most recent) among registered users
  let playersToUse = players;
  if (n % 2 === 1) {
    const byFirstLogin = [...players].sort((a, b) => {
      const ta = firstLoginByPlayerId[a.playerId] ?? 0;
      const tb = firstLoginByPlayerId[b.playerId] ?? 0;
      return ta - tb;
    });
    playersToUse = byFirstLogin.slice(0, -1);
  }

  // Sort by skill level value for pairing
  playersToUse.sort((a, b) => a.skillLevelValue - b.skillLevelValue);

  // Create teams by pairing players from opposite ends of skill spectrum
  // This ensures balanced teams (best + worst, second best + second worst, etc.)
  for (let i = 0; i < teamCount; i++) {
    const player1 = playersToUse[i];
    const player2 = playersToUse[playersToUse.length - 1 - i];
    
    const teamId = `team-${i + 1}`;
    const teamName = `Team ${i + 1}`;
    const avgSkill = (player1.skillLevelValue + player2.skillLevelValue) / 2;
    
    teams.push({
      teamId,
      teamName,
      players: [player1.playerId, player2.playerId],
      averageSkill: avgSkill,
      skillLevels: [player1.skillLevel, player2.skillLevel]
    });
  }

  return teams;
}

/**
 * Assign teams to doubles registrations
 */
export async function assignTeamsToRegistrations(teams, doublesRegs) {
  for (let i = 0; i < teams.length; i++) {
    const team = teams[i];
    for (const playerId of team.players) {
      const registration = doublesRegs.find(reg => reg.playerId === playerId);
      if (registration) {
        await updateDoc(doc(db, 'registrations', registration.id), {
          teamId: team.teamId,
          teamName: team.teamName
        });
      }
    }
  }
}

